import { Box } from "@mui/material"
import GoogleMapReact from 'google-map-react'
import { useStyles } from "./styles"
import { ActivityMapProps } from "./types"
import { useEffect, useMemo, useRef } from "react";
import Loader from "../../../../../../../components/loader";
import { Constants } from "../../../../../../../utils/constants";
import { MapMarkerActivity } from "../../../../../../../components/markMap/mapMarkActivity";
import { MapPointUser } from "../mapPointUser";
import { getStatus } from "../../../../../../../hooks/status";
import { useTranslation } from "react-i18next";
import { useContextState } from "../../../../../../../hooks/theme";
import { Status } from "../../../../../../../utils/enums";

export const ActivityMap = ({ usersMark = [], hiddenUsers = [], loadings }: ActivityMapProps) => {
    const userDisplayed = useMemo(() => usersMark.filter(user => !hiddenUsers.some(userId => userId == user.user.id)) || [], [usersMark, hiddenUsers])
    const fistCordinates = userDisplayed?.find(user => user?.points?.length > 0)?.points?.[0] ?? Constants.mapDefault?.center;
    const styles = useStyles()
    const { t } = useTranslation()
    const { colors } = useContextState()
    const mapRef = useRef<google.maps.Map | null>(null);
    const mapsRef = useRef<typeof google.maps | null>(null);
    const directionsRenderersRef = useRef<google.maps.DirectionsRenderer[]>([]);
    const clearPreviousDirections = () => {
        directionsRenderersRef.current.forEach(renderer => renderer.setMap(null));
        directionsRenderersRef.current = [];
    };

    /* const groupedUserPoints = useMemo(() => {
        const map: Record<string, { color: number; points: { lat: number; lng: number }[] }> = {};

        userDisplayed.forEach(user => {
            const userId = user.user.userId;
            if (!map[userId]) {
                map[userId] = { color: user.color, points: [] };
            }
            map[userId].points.push(...(user.points || []));
        });

        return map;
    }, [userDisplayed]); */

    const handleApiLoaded = ({ map, maps }: { map: google.maps.Map; maps: typeof google.maps }) => {
        mapRef.current = map;
        mapsRef.current = maps;
        clearPreviousDirections();

        // Agrupar puntos por userId
        const groupedByUserId: Record<string, { lat: number; lng: number; color: number, index: number }[]> = {};
        const userIndexMap = new Map();

        userDisplayed.forEach((user) => {
            const userId = user.user.userId;
            userIndexMap.set(userId, user.index);
            if (!groupedByUserId[userId]) {
                groupedByUserId[userId] = [];
            }
            if (user.points) {
                const coloredPoints = user.points.map((point) => ({
                    ...point,
                    index: user.index,
                    color: user.color, // Asignar el color del usuario a cada punto
                }));
                groupedByUserId[userId].push(...coloredPoints);
                //groupedByUserId[userId].push(...user.points);
            }
        });

        const sortedGroupedByUserId = Object.fromEntries(
            Object.entries(groupedByUserId).map(([userId, points]) => [
                userId,
                points.slice().sort((a, b) => a.index - b.index)
            ])
        );

        // Trazar líneas por cada usuario
        Object.entries(sortedGroupedByUserId).forEach(([userId, points]) => {
            for (let i = 0; i < points.length - 1; i++) {
                const origin = { lat: points[i].lat, lng: points[i].lng };
                const destination = { lat: points[i + 1].lat, lng: points[i + 1].lng };

                // Usar el color del punto destino
                const { color: strokeColor } = getStatus(points[i + 1].color===Status.NOT_FINISHED ? Status.CANCEL:points[i + 1].color, colors, t);

                const directionsService = new maps.DirectionsService();
                const directionsRenderer = new maps.DirectionsRenderer({
                    suppressMarkers: true,
                    preserveViewport: true,
                    polylineOptions: {
                        strokeColor:colors.color4,
                        strokeWeight: 3/*4*/,
                    },
                });

                directionsRenderer.setMap(map);
                directionsRenderersRef.current.push(directionsRenderer);

                directionsService.route(
                    {
                        origin,
                        destination,
                        travelMode: maps.TravelMode.DRIVING,
                    },
                    (result, status) => {
                        if (status === maps.DirectionsStatus.OK) {
                            directionsRenderer.setDirections(result);
                        } else {
                            console.error("Error al obtener la ruta:", status);
                        }
                    }
                );
            }
        });
    };

    useEffect(() => {
        if (mapRef.current && mapsRef.current) {
            handleApiLoaded({ map: mapRef.current, maps: mapsRef.current });
        }
    }, [userDisplayed]);
    if (loadings?.right) { return <Loader /> }

    return (
        <Box sx={styles.map}>
            <GoogleMapReact
                bootstrapURLKeys={{ key: process.env.REACT_APP_MAP_APIKEY ?? '', }}
                defaultCenter={Constants.mapDefault?.center}
                center={fistCordinates}
                zoom={14}
                onGoogleApiLoaded={handleApiLoaded}
                yesIWantToUseGoogleMapApiInternals
            >
                {userDisplayed?.flatMap((user) => [
                    ...user.points?.map((point, idx) => (
                        <MapMarkerActivity
                            key={`${user.user.id}-${idx}`}
                            Node={() => (<MapPointUser extraData={point.extraData ?? ''} count={point.count} status={point.statusPoint} type={point.type} useData={point.userData} key={idx} />)}
                            {...point}
                        />
                    )) || [],
                ])}
            </GoogleMapReact>
        </Box>
    )
}